import React, { useState, useEffect, useRef } from 'react';
import StatCard from './StatCard';
import VehicleCard from './VehicleCard';
import { Vehicle, DashboardStats } from '../../types';
import { initialVehicles, initialStats } from '../../data/mockData';
import { formatToKoreanUnit } from '../../utils/formatters';
import { fetchVehicles, fetchNewVehicles } from '../../utils/api';

function Dashboard() {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [stats, setStats] = useState<DashboardStats>(initialStats);
  const [newVehicleIds, setNewVehicleIds] = useState<string[]>([]);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // 이전에 가져온 차량 ID를 추적하기 위한 ref
  const vehicleIdsRef = useRef<Set<string>>(new Set());
  // 초기 로딩 여부 추적
  const isFirstLoadRef = useRef<boolean>(true);

  // API에서 데이터 가져오기
  useEffect(() => {
    const loadVehicles = async () => {
      try {
        // 첫 로딩인 경우 전체 데이터 가져오기, 아니면 새 데이터만 가져오기
        const apiVehicles = isFirstLoadRef.current
          ? await fetchVehicles(true) // 전체 데이터
          : await fetchNewVehicles();  // 새 데이터만
        
        // 첫 로딩 후에는 false로 설정
        if (isFirstLoadRef.current) {
          isFirstLoadRef.current = false;
        }
        
        if (apiVehicles.length > 0) {
          // 현재 저장된 차량 ID 세트
          const currentIds = vehicleIdsRef.current;
          
          // 새로 추가된 차량 식별
          const newVehicles = apiVehicles.filter(v => !currentIds.has(v.id));
          
          // 새로운 ID 목록 업데이트 (새로 추가된 차량만)
          if (newVehicles.length > 0) {
            console.log(`새로 추가된 차량: ${newVehicles.length}대`);
            const newIds = newVehicles.map(v => v.id);
            setNewVehicleIds(newIds);
            
            // 새 차량 ID를 ref에 추가
            newVehicles.forEach(v => currentIds.add(v.id));
            
            // 5초 후에 '신규' 표시 제거
            setTimeout(() => {
              setNewVehicleIds([]);
            }, 5000);
          }
          
          // 첫 로딩이거나 새 차량이 있는 경우만 상태 업데이트
          if (isLoading || newVehicles.length > 0) {
            // 기존 차량과 새 차량 병합
            const mergedVehicles = [...vehicles, ...newVehicles];
            
            // 항상 최신 등록순(registeredAt)으로 정렬
            const sortedVehicles = mergedVehicles.sort((a, b) => 
              new Date(b.registeredAt).getTime() - new Date(a.registeredAt).getTime()
            );
            
            // 통계 업데이트
            const totalValue = sortedVehicles.reduce((sum, vehicle) => sum + vehicle.price, 0);
            
            setVehicles(sortedVehicles);
            setStats({
              totalVehicles: sortedVehicles.length,
              totalValue: totalValue
            });
          }
          
          // 로딩 상태 해제
          if (isLoading) {
            setIsLoading(false);
          }
          
        } else if (isLoading) {
          // 첫 로딩 시 API 실패한 경우에만 초기 데이터 사용
          setVehicles(initialVehicles);
          initialVehicles.forEach(v => vehicleIdsRef.current.add(v.id));
          setIsLoading(false);
          isFirstLoadRef.current = false;
        }
        
      } catch (err) {
        console.error('데이터 로딩 중 오류:', err);
        
        if (isLoading) {
          // 첫 로딩 시 에러가 발생한 경우에만 오류 표시 및 초기 데이터 사용
          setError('차량 데이터를 가져오는데 실패했습니다. 나중에 다시 시도해주세요.');
          setVehicles(initialVehicles);
          initialVehicles.forEach(v => vehicleIdsRef.current.add(v.id));
          setIsLoading(false);
          isFirstLoadRef.current = false;
        }
      }
      
      if (isUpdating) {
        // 업데이트 애니메이션 종료
        setTimeout(() => setIsUpdating(false), 1000);
      }
    };
    
    // 초기 데이터 로딩
    loadVehicles();
    
    // 주기적인 데이터 새로고침 설정 (30초마다)
    const refreshInterval = setInterval(() => {
      setIsUpdating(true);
      loadVehicles();
    }, 30000);
    
    return () => {
      clearInterval(refreshInterval);
    };
  }, [isLoading, isUpdating, vehicles]);
  
  // 아이콘 컴포넌트들
  const icons = {
    totalVehicles: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
      </svg>
    ),
    totalValue: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <header className="mb-8">
          <h1 className="text-3xl font-bold">AutoProof 대시보드</h1>
          <p className="text-gray-400 mt-2">차량 등록 현황 실시간 모니터링</p>
        </header>
        
        {/* 통계 카드 섹션 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          <StatCard 
            title="총 등록 차량" 
            value={stats.totalVehicles}
            icon={icons.totalVehicles}
            isAnimated={isUpdating}
          />
          <StatCard 
            title="총 차량 가액" 
            value={formatToKoreanUnit(stats.totalValue)}
            icon={icons.totalValue}
            isAnimated={isUpdating}
          />
        </div>
        
        {/* 등록 차량 리스트 */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">등록된 차량 목록</h2>
            <div className="text-gray-400 text-sm">총 {vehicles.length}대 등록됨</div>
          </div>
          
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-pulse-custom bg-blue-500/20 rounded-full h-16 w-16 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          ) : error ? (
            <div className="bg-red-500/10 text-red-400 p-4 rounded-lg">
              {error}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {vehicles.map(vehicle => (
                <VehicleCard 
                  key={vehicle.id} 
                  vehicle={vehicle} 
                  isNew={newVehicleIds.includes(vehicle.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard; 